import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Avatar,
  Link,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import BuildIcon from "@mui/icons-material/Build";
import MemoryIcon from "@mui/icons-material/Memory";
import PublicIcon from "@mui/icons-material/Public";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GavelIcon from "@mui/icons-material/Gavel";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DownloadIcon from "@mui/icons-material/Download";
import { motion, AnimatePresence } from "framer-motion";
import {
  startNegotiation,
  streamNegotiation,
  addHumanInput,
} from "../api/negotiation";

// Custom frosty white scrollbar styles
const scrollbarStyles = `
  .frosty-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.5) rgba(255,255,255,0.08);
  }
  .frosty-scrollbar::-webkit-scrollbar {
    width: 10px;
    background: rgba(255,255,255,0.04);
  }
  .frosty-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.12) 100%);
    border-radius: 8px;
    border: 2px solid rgba(255,255,255,0.18);
    box-shadow: 0 2px 8px rgba(255,255,255,0.10);
  }
  .frosty-scrollbar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.18) 100%);
  }
`;

// Content formatting helpers from AgentChat
const cleanMarkdown = (text) => {
  if (!text) return '';

  // Remove ALL markdown formatting
  let cleaned = text
    // Remove code blocks completely
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code backticks
    .replace(/`([^`]+)`/g, '$1')
    // Remove bold
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    // Remove italics
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // Remove headers
    .replace(/^#+\s/gm, '')
    // Remove links but keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove emojis completely
    .replace(/[\u{1F000}-\u{1F9FF}]/gu, '')
    .replace(/[\u{2600}-\u{27BF}]/gu, '')
    .trim();

  return cleaned;
};

const formatContent = (text, accentColor) => {
  if (!text || text.trim() === '') {
    return { content: [], urls: [] };
  }

  // Extract URLs BEFORE cleaning markdown
  const urlRegex = /(https?:\/\/[^\s\)]+)/g;
  const urls = [...text.matchAll(urlRegex)].map(match => match[1]);

  // Now clean markdown
  const cleaned = cleanMarkdown(text);

  // Split by double newlines to preserve paragraph breaks
  const paragraphs = cleaned.split('\n\n').filter(p => p.trim());

  const content = paragraphs.map((paragraph, pIdx) => {
    const trimmed = paragraph.trim();

    // Split paragraph into lines for list formatting
    const lines = trimmed.split('\n').filter(line => line.trim());

    return (
      <Box key={pIdx} sx={{ mb: 1 }}>
        {lines.map((line, lIdx) => {
          const lineTrimmed = line.trim();

          // Check if line contains URL
          const lineUrls = [...lineTrimmed.matchAll(urlRegex)];
          if (lineUrls.length > 0) {
            // Replace URLs with clickable links
            let processedLine = lineTrimmed;
            lineUrls.forEach(match => {
              const url = match[1];
              processedLine = processedLine.replace(url, `[LINK:${url}]`);
            });

            const parts = processedLine.split(/\[LINK:(https?:\/\/[^\]]+)\]/);
            return (
              <Typography key={lIdx} variant="body2" sx={{ mb: 0.5, lineHeight: 1.6, fontSize: '0.875rem' }}>
                {parts.map((part, i) => {
                  if (part.match(/^https?:\/\//)) {
                    return (
                      <Link key={i} href={part} target="_blank" rel="noopener noreferrer" sx={{ color: accentColor, textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.3, '&:hover': { textDecoration: 'underline' } }}>
                        {new URL(part).hostname.replace('www.', '')}
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                      </Link>
                    );
                  }
                  return <span key={i}>{part}</span>;
                })}
              </Typography>
            );
          }

          // Bullet points
          if (lineTrimmed.startsWith('•') || lineTrimmed.startsWith('-') || lineTrimmed.startsWith('*')) {
            const content = lineTrimmed.replace(/^[•\-*]\s*/, '');
            return (
              <Box key={lIdx} sx={{ mb: 0.5, pl: 0.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, mt: 0.7, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ lineHeight: 1.6, flex: 1, color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  {content}
                </Typography>
              </Box>
            );
          }

          // Numbered lists
          if (/^\d+[\.)]\s/.test(lineTrimmed)) {
            const num = lineTrimmed.match(/^\d+/)[0];
            const content = lineTrimmed.replace(/^\d+[\.)]\s*/, '');
            return (
              <Box key={lIdx} sx={{ mb: 0.5, pl: 0.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ color: accentColor, fontWeight: 700, minWidth: 24, lineHeight: 1.6, fontSize: '0.875rem' }}>
                  {num}.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, flex: 1, color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  {content}
                </Typography>
              </Box>
            );
          }

          // Bold text (key-value pairs like "Source: value")
          if (lineTrimmed.includes(':')) {
            const colonIdx = lineTrimmed.indexOf(':');
            const key = lineTrimmed.substring(0, colonIdx);
            const value = lineTrimmed.substring(colonIdx + 1);

            if (key.length < 30 && key.length > 0) {
              return (
                <Typography key={lIdx} variant="body2" sx={{ mb: 0.5, lineHeight: 1.6, fontSize: '0.875rem' }}>
                  <Box component="span" sx={{ fontWeight: 700, color: accentColor }}>{key}:</Box>
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.9)' }}>{value}</Box>
                </Typography>
              );
            }
          }

          // Regular line
          return (
            <Typography key={lIdx} variant="body2" sx={{ mb: 0.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
              {lineTrimmed}
            </Typography>
          );
        })}
      </Box>
    );
  });

  return { content, urls };
};

const NegotiationRoom = () => {
  const [prompt, setPrompt] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [messages, setMessages] = useState([]);
  const [deal, setDeal] = useState(null);
  const [contextPack, setContextPack] = useState(null);
  const [currentTurn, setCurrentTurn] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [contextPackExpanded, setContextPackExpanded] = useState(false); // For expandable context pack
  const [humanInputOpen, setHumanInputOpen] = useState(false); // For human interjection
  const [humanInputText, setHumanInputText] = useState(""); // Human input content
  const [negotiationId, setNegotiationId] = useState(null); // Store negotiation ID for human input endpoint
  const [isPaused, setIsPaused] = useState(false); // Pause/resume control
  const isPausedRef = useRef(false); // Ref for checking pause state in closures
  const eventBufferRef = useRef([]); // Buffer events when paused
  const eventSourceRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Configuration state - split into agents and moderator
  const [agents, setAgents] = useState([
    { role: "accuracy_advocate", provider: "anthropic", model: "claude-3-5-haiku-20241022", temperature: 0.2, top_p: 1.0 },
    { role: "grounding_scout", provider: "google", model: "gemini-2.5-flash", temperature: 0.2, top_p: 1.0 },
  ]);

  const [moderator, setModerator] = useState(
    { role: "consensus", provider: "openai", model: "gpt-4o-mini", temperature: 0.2, top_p: 1.0 }
  );

  const [rounds, setRounds] = useState(2); // Number of debate rounds (1-3) - Default to 2 for human input

  // Available models per provider
  const availableModels = {
    openai: [
      { id: "gpt-4o-mini", name: "GPT-4o Mini", inputPrice: 0.15, outputPrice: 0.60 },
      { id: "gpt-4o", name: "GPT-4o", inputPrice: 2.50, outputPrice: 10.00 },
      { id: "gpt-4-turbo", name: "GPT-4 Turbo", inputPrice: 10.00, outputPrice: 30.00 },
      { id: "o1-mini", name: "o1 Mini (Reasoning)", inputPrice: 3.00, outputPrice: 12.00 },
      { id: "o1-preview", name: "o1 Preview (Reasoning)", inputPrice: 15.00, outputPrice: 60.00 },
      { id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo", inputPrice: 0.50, outputPrice: 1.50 },
    ],
    anthropic: [
      { id: "claude-3-haiku-20240307", name: "Claude 3 Haiku", inputPrice: 0.25, outputPrice: 1.25 },
      { id: "claude-3-5-haiku-20241022", name: "Claude 3.5 Haiku", inputPrice: 0.80, outputPrice: 4.00 },
      { id: "claude-3-5-sonnet-20241022", name: "Claude 3.5 Sonnet", inputPrice: 3.00, outputPrice: 15.00 },
      { id: "claude-3-opus-20240229", name: "Claude 3 Opus", inputPrice: 15.00, outputPrice: 75.00 },
    ],
    google: [
      { id: "gemini-2.0-flash-exp", name: "Gemini 2.0 Flash (Exp) ≈Free*", inputPrice: 0, outputPrice: 0, note: "Free during preview" },
      { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash ≈Free*", inputPrice: 0, outputPrice: 0, note: "Free during preview" },
      { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash", inputPrice: 0.075, outputPrice: 0.30 },
      { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro", inputPrice: 1.25, outputPrice: 5.00 },
    ],
    xai: [
      { id: "grok-beta", name: "Grok Beta", inputPrice: 5.00, outputPrice: 15.00, approx: true },
      { id: "grok-2-latest", name: "Grok 2 Latest", inputPrice: 2.00, outputPrice: 10.00, approx: true },
      { id: "grok-vision-beta", name: "Grok Vision Beta", inputPrice: 5.00, outputPrice: 15.00, approx: true },
    ],
  };

  // Helper to get cost tier for visual indicators
  const getCostTier = (model) => {
    if (model.inputPrice === 0 && model.outputPrice === 0) {
      return { tier: "free", label: "Free*", color: "#26bf94", bgColor: "rgba(76, 175, 80, 0.15)" };
    }
    const avgCost = (model.inputPrice + model.outputPrice) / 2;
    if (avgCost < 1.0) {
      return { tier: "budget", label: "Budget", color: "#2196f3", bgColor: "rgba(33, 150, 243, 0.15)" };
    } else if (avgCost < 10.0) {
      return { tier: "mid", label: "Mid-tier", color: "#ff9800", bgColor: "rgba(255, 152, 0, 0.15)" };
    } else {
      return { tier: "premium", label: "Premium", color: "#e91e63", bgColor: "rgba(233, 30, 99, 0.15)" };
    }
  };

  const roleOptions = [
    // Debating Agents
    { id: "accuracy_advocate", name: "Accuracy Advocate", icon: GavelIcon, description: "Focuses on factual correctness", type: "agent" },
    { id: "grounding_scout", name: "Grounding Scout", icon: TrackChangesIcon, description: "Ensures evidence backing", type: "agent" },
    { id: "cost_keeper", name: "Cost Keeper", icon: AttachMoneyIcon, description: "Optimizes for efficiency", type: "agent" },
    { id: "creative_thinker", name: "Creative Thinker", icon: TrendingUpIcon, description: "Explores novel approaches", type: "agent" },
    { id: "skeptic", name: "Skeptic", icon: GavelIcon, description: "Challenges assumptions critically", type: "agent" },
    { id: "pragmatist", name: "Pragmatist", icon: BuildIcon, description: "Focuses on practical implementation", type: "agent" },

    // Moderators
    { id: "judge", name: "Judge", icon: GavelIcon, description: "Evaluates arguments and decides", type: "moderator" },
    { id: "consensus", name: "Consensus Builder", icon: CheckCircleIcon, description: "Finds common ground and synthesizes", type: "moderator" },
    { id: "summarizer", name: "Summarizer", icon: AssignmentIcon, description: "Distills key points clearly", type: "moderator" },
  ];

  const handleRoleChange = (index, field, value) => {
    const newAgents = [...agents];
    newAgents[index] = { ...newAgents[index], [field]: value };

    // If provider changed, reset model to first available
    if (field === 'provider') {
      newAgents[index].model = availableModels[value][0].id;
    }

    setAgents(newAgents);
  };

  const handleModeratorChange = (field, value) => {
    const newModerator = { ...moderator, [field]: value };

    // If provider changed, reset model to first available
    if (field === 'provider') {
      newModerator.model = availableModels[value][0].id;
    }

    setModerator(newModerator);
  };

  const addAgent = () => {
    if (agents.length < 5) {
      setAgents([...agents, {
        role: "cost_keeper",
        provider: "openai",
        model: "gpt-4o-mini",
        temperature: 0.2,
        top_p: 1.0
      }]);
    }
  };

  const removeAgent = (index) => {
    if (agents.length > 2) {
      const newAgents = agents.filter((_, i) => i !== index);
      setAgents(newAgents);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const exportTranscript = () => {
    if (!messages.length) return;

    // Build markdown content
    let markdown = `# Negotiation Transcript\n\n`;
    markdown += `**Question:** ${prompt}\n\n`;
    markdown += `**Date:** ${new Date().toLocaleString()}\n\n`;
    
    if (contextPack) {
      markdown += `## Context Pack\n\n`;
      markdown += `- **Tokens:** ${contextPack.tokens}\n`;
      markdown += `- **Cost:** $${(contextPack.est_cost_cents / 100).toFixed(5)}\n`;
      markdown += `- **Sources:** ${contextPack.sources?.length || 0}\n\n`;
    }

    markdown += `## Debate\n\n`;
    
    messages.forEach((msg, idx) => {
      const agentName = getAgentName(msg.agent);
      const roundInfo = msg.round === "final" ? "Final Evaluation" : `Round ${msg.round}`;
      
      markdown += `### ${agentName} (${roundInfo})\n\n`;
      markdown += `${msg.message}\n\n`;
      
      if (msg.cost !== undefined) {
        markdown += `*Cost: $${(msg.cost / 100).toFixed(5)}*\n\n`;
      }
      
      markdown += `---\n\n`;
    });

    if (deal) {
      markdown += `## Final Decision\n\n`;
      markdown += `**Answer:** ${deal.consensus_answer}\n\n`;
      markdown += `**Rationale:** ${deal.consensus_rationale}\n\n`;
      markdown += `**Total Cost:** $${(deal.total_cost_cents / 100).toFixed(5)}\n`;
    }

    // Create download
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `negotiation-${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Human interjection handler
  const handleHumanInput = async () => {
    if (!humanInputText.trim() || !negotiationId) {
      console.log(`[HUMAN INPUT] Skipped - text: ${humanInputText.trim().length} chars, negId: ${negotiationId}`);
      return;
    }

    try {
      console.log(`[HUMAN INPUT] Sending to backend: "${humanInputText}"`);
      // Send to backend - don't add to UI yet, let SSE event handle it
      const result = await addHumanInput(negotiationId, humanInputText);
      console.log(`[HUMAN INPUT] Backend response:`, result);

      // Clear input and close
      setHumanInputText("");
      setHumanInputOpen(false);
      
      // Auto-resume after submitting
      console.log(`[HUMAN INPUT] Auto-resuming...`);
      handlePauseResume();

    } catch (error) {
      console.error("Failed to send human input:", error);
      alert("Failed to send your input. Please try again.");
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStartNegotiation = async () => {
    if (!prompt.trim()) return;
    
    // Validate minimum agents
    if (agents.length < 2) {
      alert("⚠️ You need at least 2 debating agents for a negotiation. Please add more agents in the configuration.");
      return;
    }

    setIsRunning(true);
    setMessages([]);
    setDeal(null);
    setContextPack(null);
    setCurrentTurn(null);
    setIsPaused(false);
    isPausedRef.current = false;
    eventBufferRef.current = [];

    try {
      const config = {
        prompt,
        roles: [...agents, moderator],  // Combine agents and moderator
        rounds: rounds,  // Number of debate rounds
      };

      console.log("Starting negotiation with config:", config);

      const { negotiation_id } = await startNegotiation(config);
      setNegotiationId(negotiation_id); // Store for human input endpoint

      const eventSource = streamNegotiation(
        negotiation_id,
        (event) => {
          // If paused, buffer the event instead of processing it
          if (isPausedRef.current) {
            console.log(`[STREAM] Event buffered (PAUSED):`, event.type);
            eventBufferRef.current.push(event);
            return;
          }
          
          // Process event normally
          console.log(`[STREAM] Event processed (RUNNING):`, event.type);
          processEvent(event);
        },
        (error) => {
          console.error("SSE error:", error);
          setIsRunning(false);
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
        }
      );

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error("Failed to start negotiation:", error);
      setIsRunning(false);
    }
  };

  // Process SSE events (extracted so we can use it for buffered events too)
  const processEvent = (event) => {
    if (event.type === "context_pack") {
      setContextPack(event.payload);
    } else if (event.type === "turn_start") {
      setCurrentTurn(event.payload);
    } else if (event.type === "agent_start") {
      // Agent started - create placeholder message for streaming
      setMessages((prev) => [...prev, {
        agent: event.payload.agent,
        message: "",
        turn: event.payload.turn,
        round: event.payload.round || event.payload.turn,
        cost: 0,
        streaming: true  // Flag to indicate this is being streamed
      }]);
    } else if (event.type === "agent_chunk") {
      // Streaming chunk - append to the last message from this agent
      setMessages((prev) => {
        const newMessages = [...prev];
        // Find the last message from this agent that's still streaming
        for (let i = newMessages.length - 1; i >= 0; i--) {
          if (newMessages[i].agent === event.payload.agent && newMessages[i].streaming) {
            newMessages[i] = {
              ...newMessages[i],
              message: newMessages[i].message + event.payload.delta
            };
            break;
          }
        }
        return newMessages;
      });
    } else if (event.type === "agent_message") {
      // Final message - update the streaming message or add new one
      setMessages((prev) => {
        const newMessages = [...prev];
        // Find the streaming message for this agent
        let found = false;
        for (let i = newMessages.length - 1; i >= 0; i--) {
          if (newMessages[i].agent === event.payload.agent && newMessages[i].streaming) {
            newMessages[i] = {
              ...event.payload,
              streaming: false  // Mark as complete
            };
            found = true;
            break;
          }
        }
        // If no streaming message found, add it (fallback for non-streaming)
        if (!found) {
          newMessages.push(event.payload);
        }
        return newMessages;
      });
    } else if (event.type === "deal") {
      setDeal(event.payload);
      setIsRunning(false);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    } else if (event.type === "error") {
      setIsRunning(false);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
  };

  // Handle pause/resume - now also opens input when pausing
  const handlePauseResume = () => {
    if (isPausedRef.current) {
      // Resume - process buffered events quickly
      console.log(`[PAUSE] Resuming - buffered ${eventBufferRef.current.length} events`);
      const buffered = [...eventBufferRef.current];
      eventBufferRef.current = [];
      
      isPausedRef.current = false;
      setIsPaused(false);
      
      // Process all buffered events
      buffered.forEach(event => {
        console.log(`[PAUSE] Processing buffered event:`, event.type);
        processEvent(event);
      });
      
      console.log(`[PAUSE] Resumed - stream should continue`);
    } else {
      // Pause - also open input dialog
      console.log(`[PAUSE] Pausing stream`);
      isPausedRef.current = true;
      setIsPaused(true);
      setHumanInputOpen(true);
    }
  };

  const getAgentColor = (role) => {
    const colors = {
      // Debating agents
      accuracy_advocate: "#7595ff",     // Blue
      grounding_scout: "#3ad0de",       // Cyan
      cost_keeper: "#D183C9",           // Purple/Pink
      creative_thinker: "#da7d9a",      // Coral (fixed - removed extra ff)
      skeptic: "#e8be42",               // Yellow
      pragmatist: "#d77d56",            // Orange

      // Moderators
      judge: "#4bb58e",                 // Green
      consensus: "#4bb58e",             // Green
      summarizer: "#4bb58e",            // Green
    };
    return colors[role] || "#7595ff";  // Default to blue instead of grey
  };

  const getAgentIcon = (role) => {
    const icons = {
      // Debating agents
      accuracy_advocate: GavelIcon,
      grounding_scout: TrackChangesIcon,
      cost_keeper: AttachMoneyIcon,
      creative_thinker: TrendingUpIcon,
      skeptic: GavelIcon,
      pragmatist: BuildIcon,

      // Moderators
      judge: GavelIcon,
      consensus: CheckCircleIcon,
      summarizer: AssignmentIcon,
    };
    return icons[role] || GavelIcon;
  };

  const getAgentName = (role) => {
    const names = {
      // Debating agents
      accuracy_advocate: "Accuracy Advocate",
      grounding_scout: "Grounding Scout",
      cost_keeper: "Cost Keeper",
      creative_thinker: "Creative Thinker",
      skeptic: "Skeptic",
      pragmatist: "Pragmatist",

      // Moderators
      judge: "Judge",
      consensus: "Consensus Builder",
      summarizer: "Summarizer",
    };
    return names[role] || role;
  };

  useEffect(() => {
    // Inject frosty scrollbar styles once
    if (!document.getElementById('frosty-scrollbar-style')) {
      const style = document.createElement('style');
      style.id = 'frosty-scrollbar-style';
      style.innerHTML = scrollbarStyles;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <Box sx={{
      height: 'calc(100vh - 180px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(88, 88, 88, 0.06)',
      backdropFilter: 'blur(20px)'
    }}>
      {/* Fixed Header with Input */}
      <Box sx={{
        flexShrink: 0,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ p: 2 }}>
          {/* Input Row */}
          <Box sx={{ display: "flex", gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="visualize “before vs after” improvements for an AI answer"
              variant="outlined"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., visualize “before vs after” improvements for an AI answer"
              size="small"
              multiline
              maxRows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.05)',
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartNegotiation}
              disabled={isRunning || !prompt.trim()}
              sx={{
                minWidth: 140,
                color: '#2d3654',
                background: 'linear-gradient(135deg, #79b8c9 0%, #5a92a0 100%)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: 'linear-gradient(135deg, #8ac9da 0%, #6ba3b1 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(121,184,201, 0.4)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {isRunning ? "Running..." : "Run"}
            </Button>
            
            {/* Export Transcript Button */}
            {messages.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportTranscript}
                sx={{
                  minWidth: 140,
                  borderColor: 'rgba(121, 184, 201, 0.5)',
                  color: '#79b8c9',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: 'rgba(121, 184, 201, 1)',
                    background: 'rgba(121, 184, 201, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Export
              </Button>
            )}
          </Box>



          {/* Collapsible Configuration Panel */}
          <Collapse in={configOpen}>
            <Box sx={{
              mt: 2,
              p: 2,
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              position: 'relative'
            }}>
              {/* Collapse Button */}
              <IconButton
                onClick={() => setConfigOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    color: '#79b8c9',
                    background: 'rgba(121, 184, 201, 0.1)'
                  }
                }}
              >
                <ExpandLessIcon />
              </IconButton>

              {/* GLOBAL SETTINGS */}
              <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.85rem',
                  mb: 1.5
                }}>
                  Debate Settings
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Number of Rounds</InputLabel>
                  <Select
                    value={rounds}
                    label="Number of Rounds"
                    onChange={(e) => setRounds(e.target.value)}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    <MenuItem value={1} sx={{ fontSize: '0.8rem' }}>1 Round (Quick)</MenuItem>
                    <MenuItem value={2} sx={{ fontSize: '0.8rem' }}>2 Rounds (Balanced)</MenuItem>
                    <MenuItem value={3} sx={{ fontSize: '0.8rem' }}>3 Rounds (Deep Debate)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" sx={{
                  display: 'block',
                  mt: 1,
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.7rem',
                  fontStyle: 'italic'
                }}>
                  Agents debate for {rounds} round{rounds > 1 ? 's' : ''}, then the moderator picks the best strategies
                </Typography>
              </Box>

              {/* SECTION 1: Debating Agents */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.85rem'
                  }}>
                    Debating Agents ({agents.length}/5)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={addAgent}
                      disabled={agents.length >= 5}
                      sx={{
                        fontSize: '0.7rem',
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        background: 'rgba(58, 208, 222, 0.2)',
                        color: '#3ad0de',
                        '&:hover': { background: 'rgba(58, 208, 222, 0.3)' },
                        '&:disabled': { opacity: 0.3 }
                      }}
                    >
                      + Add Agent
                    </Button>
                  </Box>
                </Box>

                {agents.map((agent, index) => {
                  const roleInfo = roleOptions.find(r => r.id === agent.role && r.type === 'agent');
                  const RoleIcon = roleInfo?.icon || GavelIcon;

                  return (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{
                            width: 28,
                            height: 28,
                            background: getAgentColor(agent.role),
                            boxShadow: `0 2px 8px ${getAgentColor(agent.role)}40`
                          }}>
                            <RoleIcon sx={{ fontSize: '1rem' }} />
                          </Avatar>
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: getAgentColor(agent.role),
                            fontSize: '0.85rem'
                          }}>
                            Agent {index + 1}
                          </Typography>
                        </Box>
                        {agents.length > 2 && (
                          <IconButton
                            size="small"
                            onClick={() => removeAgent(index)}
                            sx={{
                              color: 'rgba(255,255,255,0.4)',
                              '&:hover': { color: '#D183C9' }
                            }}
                          >
                            <ExpandLessIcon sx={{ fontSize: '1rem', transform: 'rotate(45deg)' }} />
                          </IconButton>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {/* Role Selection */}
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Role</InputLabel>
                          <Select
                            value={agent.role}
                            label="Role"
                            onChange={(e) => handleRoleChange(index, 'role', e.target.value)}
                            sx={{
                              fontSize: '0.8rem',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${getAgentColor(agent.role)}40`
                              }
                            }}
                          >
                            {roleOptions.filter(opt => opt.type === 'agent').map(option => (
                              <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.8rem' }}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Provider Selection */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Provider</InputLabel>
                          <Select
                            value={agent.provider}
                            label="Provider"
                            onChange={(e) => handleRoleChange(index, 'provider', e.target.value)}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            <MenuItem value="openai" sx={{ fontSize: '0.8rem' }}>OpenAI</MenuItem>
                            <MenuItem value="anthropic" sx={{ fontSize: '0.8rem' }}>Anthropic</MenuItem>
                            <MenuItem value="google" sx={{ fontSize: '0.8rem' }}>Google</MenuItem>
                            <MenuItem value="xai" sx={{ fontSize: '0.8rem' }}>xAI</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Model Selection */}
                        <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Model</InputLabel>
                          <Select
                            value={agent.model}
                            label="Model"
                            onChange={(e) => handleRoleChange(index, 'model', e.target.value)}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            {availableModels[agent.provider]?.map(model => {
                              const costTier = getCostTier(model);
                              // Format pricing for clear display with labels
                              const formatPrice = (price) => {
                                if (price === 0) return 'Free';
                                if (price < 1) return `$${price.toFixed(3).replace(/\.?0+$/, '')}`;
                                return `$${price.toFixed(2).replace(/\.?0+$/, '')}`;
                              };

                              return (
                                <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.8rem' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                                    <span>{model.name}</span>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      {/* Cost Tier Badge */}
                                      <Chip
                                        label={costTier.label}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.6rem',
                                          borderColor: costTier.color,
                                          color: costTier.color,
                                          fontWeight: 600,
                                          backgroundColor: costTier.bgColor,
                                          '& .MuiChip-label': { px: 0.75 }
                                        }}
                                      />
                                      {/* Pricing Details */}
                                      {costTier.tier !== "free" ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.6rem', 
                                            color: 'rgba(255,255,255,0.6)', 
                                            lineHeight: 1,
                                            fontFamily: 'monospace',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            In: {formatPrice(model.inputPrice)}/1M
                                          </Typography>
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.6rem', 
                                            color: 'rgba(255,255,255,0.6)', 
                                            lineHeight: 1,
                                            fontFamily: 'monospace',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            Out: {formatPrice(model.outputPrice)}/1M{model.approx && ' ≈'}
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Typography variant="caption" sx={{ 
                                          fontSize: '0.65rem', 
                                          color: '#26bf947c', 
                                          lineHeight: 1, 
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap'
                                        }}>
                                          Free (preview)
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>

                      {roleInfo && (
                        <Typography variant="caption" sx={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.7rem',
                          mt: 0.5,
                          display: 'block',
                          ml: 4.5
                        }}>
                          {roleInfo.description}
                        </Typography>
                      )}

                      {index < agents.length - 1 && <Divider sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.1)' }} />}
                    </Box>
                  );
                })}
              </Box>

              {/* SECTION 2: Moderator */}
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  mb: 2,
                  fontSize: '0.85rem'
                }}>
                  Moderator / Judge
                </Typography>

                {(() => {
                  const roleInfo = roleOptions.find(r => r.id === moderator.role && r.type === 'moderator');
                  const RoleIcon = roleInfo?.icon || GavelIcon;

                  return (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{
                          width: 28,
                          height: 28,
                          background: getAgentColor(moderator.role),
                          boxShadow: `0 2px 8px ${getAgentColor(moderator.role)}40`
                        }}>
                          <RoleIcon sx={{ fontSize: '1rem' }} />
                        </Avatar>
                        <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: getAgentColor(moderator.role),
                          fontSize: '0.85rem'
                        }}>
                          Moderator
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {/* Role Selection */}
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Role</InputLabel>
                          <Select
                            value={moderator.role}
                            label="Role"
                            onChange={(e) => handleModeratorChange('role', e.target.value)}
                            sx={{
                              fontSize: '0.8rem',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${getAgentColor(moderator.role)}40`
                              }
                            }}
                          >
                            {roleOptions.filter(opt => opt.type === 'moderator').map(option => (
                              <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.8rem' }}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Provider Selection */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Provider</InputLabel>
                          <Select
                            value={moderator.provider}
                            label="Provider"
                            onChange={(e) => handleModeratorChange('provider', e.target.value)}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            <MenuItem value="openai" sx={{ fontSize: '0.8rem' }}>OpenAI</MenuItem>
                            <MenuItem value="anthropic" sx={{ fontSize: '0.8rem' }}>Anthropic</MenuItem>
                            <MenuItem value="google" sx={{ fontSize: '0.8rem' }}>Google</MenuItem>
                            <MenuItem value="xai" sx={{ fontSize: '0.8rem' }}>xAI</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Model Selection */}
                        <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                          <InputLabel sx={{ fontSize: '0.8rem' }}>Model</InputLabel>
                          <Select
                            value={moderator.model}
                            label="Model"
                            onChange={(e) => handleModeratorChange('model', e.target.value)}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            {availableModels[moderator.provider]?.map(model => {
                              const costTier = getCostTier(model);
                              // Format pricing for clear display with labels
                              const formatPrice = (price) => {
                                if (price === 0) return 'Free';
                                if (price < 1) return `$${price.toFixed(3).replace(/\.?0+$/, '')}`;
                                return `$${price.toFixed(2).replace(/\.?0+$/, '')}`;
                              };

                              return (
                                <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.8rem' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                                    <span>{model.name}</span>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      {/* Cost Tier Badge */}
                                      <Chip
                                        label={costTier.label}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.6rem',
                                          borderColor: costTier.color,
                                          color: costTier.color,
                                          fontWeight: 600,
                                          backgroundColor: costTier.bgColor,
                                          '& .MuiChip-label': { px: 0.75 }
                                        }}
                                      />
                                      {/* Pricing Details */}
                                      {costTier.tier !== "free" ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.6rem', 
                                            color: 'rgba(255,255,255,0.6)', 
                                            lineHeight: 1,
                                            fontFamily: 'monospace',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            In: {formatPrice(model.inputPrice)}/1M
                                          </Typography>
                                          <Typography variant="caption" sx={{ 
                                            fontSize: '0.6rem', 
                                            color: 'rgba(255,255,255,0.6)', 
                                            lineHeight: 1,
                                            fontFamily: 'monospace',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            Out: {formatPrice(model.outputPrice)}/1M{model.approx && ' ≈'}
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Typography variant="caption" sx={{ 
                                          fontSize: '0.65rem', 
                                          color: 'rgba(38, 191, 148, 0.8)', 
                                          lineHeight: 1, 
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap'
                                        }}>
                                          Free (preview)
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>

                      {roleInfo && (
                        <Typography variant="caption" sx={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.7rem',
                          mt: 0.5,
                          display: 'block',
                          ml: 4.5
                        }}>
                          {roleInfo.description}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          </Collapse>

          {isRunning && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress sx={{ 
                height: 2,
                background: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(135deg, #79b8c9 0%, #5a92a0 100%)',
                }
              }} />
            </Box>
          )}

          {deal && (
            <Alert severity="success" sx={{
              mt: 0.5,
              background: 'rgba(76, 175, 80, 0.01)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(76, 175, 80, 0.05)',
              borderRadius: '8px',
              py: 0.5
            }}>
              Consensus reached! Confidence: {(deal.confidence * 100).toFixed(0)}%
            </Alert>
          )}
        </Box>
      </Box>

      {/* Conversation Thread */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0
      }}>
        {/* Context Pack Banner - Enhanced with expand/collapse */}
        {contextPack && (
          <Box sx={{
            flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(58, 208, 222, 0.1) 0%, rgba(46, 166, 177, 0.1) 100%)',
            borderBottom: '1px solid rgba(58, 208, 222, 0.3)',
          }}>
            {/* Header - Always visible */}
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                cursor: 'pointer',
                '&:hover': {
                  background: 'rgba(58, 208, 222, 0.05)'
                }
              }}
              onClick={() => setContextPackExpanded(!contextPackExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BuildIcon sx={{ fontSize: '1.1rem', color: '#3ad0de' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#3ad0de', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Context Gathered:
                </Typography>
                <Chip
                  label={`${contextPack.sources?.length || 0} sources`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    background: 'rgba(58, 208, 222, 0.3)',
                    color: '#3ad0de',
                    border: '1px solid rgba(58, 208, 222, 0.5)',
                  }}
                />
              </Box>
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                  {contextPack.tokens} tokens • ${(contextPack.est_cost_cents / 100).toFixed(5)}
                </Typography>
                <Typography sx={{ color: '#3ad0de', fontSize: '0.8rem' }}>
                  {contextPackExpanded ? '▼' : '▶'}
                </Typography>
              </Box>
            </Box>

            {/* Expandable Details */}
            {contextPackExpanded && (
              <Box sx={{
                p: 2,
                pt: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                borderTop: '1px solid rgba(58, 208, 222, 0.2)',
                background: 'rgba(0, 0, 0, 0.2)'
              }}>
                {contextPack.sources?.map((source, idx) => {
                  const isMemory = source.kind === 'memory';
                  const isWeb = source.kind === 'url';
                  const label = isMemory
                    ? (source.ref.includes('memory:') ? source.ref.split('memory:')[1].replace(/_/g, ' ') : 'Memory Search')
                    : (source.ref.includes('web:') ? 'Web Search Results' : source.ref);

                  return (
                    <Paper
                      key={idx}
                      sx={{
                        p: 1.5,
                        background: 'rgba(58, 208, 222, 0.05)',
                        border: '1px solid rgba(58, 208, 222, 0.2)',
                        borderRadius: '8px'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {isMemory ? (
                          <MemoryIcon sx={{ fontSize: '1rem', color: '#3ad0de' }} />
                        ) : (
                          <PublicIcon sx={{ fontSize: '1rem', color: '#3ad0de' }} />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#3ad0de', fontSize: '0.75rem' }}>
                          {isMemory ? 'Memory' : 'Web Source'}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '0.75rem',
                          wordBreak: 'break-all',
                          fontFamily: isMemory ? 'inherit' : 'monospace'
                        }}
                      >
                        {label}
                      </Typography>
                      {source.fetched_at && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
                          Fetched: {new Date(source.fetched_at).toLocaleString()}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Conversation Messages */}
        <Box
          className="frosty-scrollbar"
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          {/* Show centered config when no messages */}
          {!isRunning && messages.length === 0 && (
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px'
            }}>
              
              <Box sx={{
                width: '100%',
                maxWidth: '800px',
                p: 3,
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(121, 184, 201, 0.3)',
                boxShadow: '0 4px 20px rgba(121, 184, 201, 0.1)'
              }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#79b8c9',
                  mb: 2,
                  textAlign: 'center'
                }}>
                  Configure Your Agents
                </Typography>
                
                {/* Show mini version of config */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mb: 1 }}>
                    Current Setup:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {agents.map((agent, idx) => {
                      const roleInfo = roleOptions.find(r => r.id === agent.role);
                      const RoleIcon = roleInfo?.icon || GavelIcon;
                      const modelInfo = availableModels[agent.provider]?.find(m => m.id === agent.model);
                      const agentColor = getAgentColor(agent.role);
                      return (
                        <Chip
                          key={`agent-preview-${idx}`}
                          icon={<RoleIcon sx={{ fontSize: '1rem' }} />}
                          label={`${roleInfo?.name || agent.role}: ${modelInfo?.name || agent.model}`}
                          size="medium"
                          sx={{
                            height: 32,
                            fontSize: '0.85rem',
                            background: `linear-gradient(135deg, ${agentColor}50 0%, ${agentColor}35 100%)`,
                            color: agentColor,
                            border: `2px solid ${agentColor}90`,
                            fontWeight: 600,
                            boxShadow: `0 2px 12px ${agentColor}35`,
                            '& .MuiChip-icon': { color: agentColor }
                          }}
                        />
                      );
                    })}
                    {(() => {
                      const roleInfo = roleOptions.find(r => r.id === moderator.role);
                      const RoleIcon = roleInfo?.icon || GavelIcon;
                      const modelInfo = availableModels[moderator.provider]?.find(m => m.id === moderator.model);
                      const modColor = getAgentColor(moderator.role);
                      return (
                        <Chip
                          key="moderator-preview"
                          icon={<RoleIcon sx={{ fontSize: '1rem' }} />}
                          label={`${roleInfo?.name || moderator.role}: ${modelInfo?.name || moderator.model}`}
                          size="medium"
                          sx={{
                            height: 32,
                            fontSize: '0.85rem',
                            background: `linear-gradient(135deg, ${modColor}50 0%, ${modColor}35 100%)`,
                            color: modColor,
                            border: `2px solid ${modColor}90`,
                            fontWeight: 600,
                            boxShadow: `0 2px 12px ${modColor}35`,
                            '& .MuiChip-icon': { color: modColor }
                          }}
                        />
                      );
                    })()}
                  </Box>
                  
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<SettingsIcon />}
                      onClick={() => setConfigOpen(!configOpen)}
                      sx={{
                        background: 'linear-gradient(135deg, #79b8c9 0%, #5a92a0 100%)',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: '#2d3654',
                        px: 4,
                        py: 1.25,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #8ac9da 0%, #6ba3b1 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(121, 184, 201, 0.4)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Customize Agent Configuration
                    </Button>
                  </Box>
                  
                  <Typography variant="caption" sx={{
                    color: 'rgba(255,255,255,0.4)',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    mt: 1
                  }}>
                    {rounds} debate round{rounds > 1 ? 's' : ''} • {agents.length} debating agent{agents.length > 1 ? 's' : ''} • 1 moderator
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          
          <AnimatePresence>
            {messages.map((msg, idx) => {
              // Special handling for human messages
              const isHuman = msg.isHuman || msg.agent === "You";
              
              // Check if this agent is replying to a human (previous message was human)
              const previousMsg = idx > 0 ? messages[idx - 1] : null;
              const isReplyingToHuman = !isHuman && previousMsg && (previousMsg.isHuman || previousMsg.agent === "You");
              
              const AgentIcon = isHuman ? () => <span style={{ fontSize: 22 }}>👤</span> : getAgentIcon(msg.agent);
              const agentColor = isHuman ? '#4ade80' : getAgentColor(msg.agent);
              const agentGradient = `linear-gradient(135deg, ${agentColor} 0%, ${agentColor}aa 100%)`;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Show "Replying to You" indicator */}
                  {isReplyingToHuman && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      ml: 2,
                      color: '#4ade80',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      opacity: 0.8
                    }}>
                      <Box sx={{
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: '#4ade80',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 0.4 },
                          '50%': { opacity: 1 }
                        }
                      }} />
                      ↪ Replying to your input
                    </Box>
                  )}
                  <Paper sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '12px',
                    boxShadow: isHuman 
                      ? `0 4px 20px ${agentColor}25, 0 2px 8px rgba(74, 222, 128, 0.2)`
                      : `0 4px 20px ${agentColor}15, 0 2px 8px rgba(0,0,0,0.1)`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: isHuman
                      ? 'linear-gradient(135deg, rgba(74, 222, 128, 0.08) 0%, rgba(74, 222, 128, 0.04) 100%)'
                      : `linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)`,
                    border: isHuman 
                      ? `2px solid ${agentColor}60`
                      : `1px solid ${agentColor}30`,
                    '&:hover': {
                      boxShadow: `0 8px 30px ${agentColor}25, 0 4px 12px rgba(0,0,0,0.15)`,
                      transform: 'translateY(-2px)',
                      border: `1px solid ${agentColor}50`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${agentColor} 0%, ${agentColor}80 100%)`,
                      boxShadow: `0 2px 8px ${agentColor}40`
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '200px',
                      height: '200px',
                      background: `radial-gradient(circle, ${agentColor}08 0%, transparent 70%)`,
                      pointerEvents: 'none'
                    }
                  }}>
                    <Box sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
                      {/* Agent Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          background: `linear-gradient(135deg, ${agentColor} 0%, ${agentColor}cc 100%)`,
                          borderRadius: '12px',
                          width: 44,
                          height: 44,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          boxShadow: `0 4px 12px ${agentColor}40, inset 0 1px 2px rgba(255,255,255,0.2)`,
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '12px',
                            padding: '2px',
                            background: `linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)`,
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                          }
                        }}>
                          <AgentIcon sx={{ color: '#fff', fontSize: 22, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{
                            fontWeight: 700,
                            color: agentColor,
                            mb: 0.25,
                            fontSize: '1rem',
                            letterSpacing: '0.01em',
                            textShadow: `0 1px 2px ${agentColor}20`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            {getAgentName(msg.agent)}
                            {isHuman && (
                              <Chip
                                label="YOU"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  fontWeight: 800,
                                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                                  color: '#000',
                                  border: '1.5px solid #4ade80',
                                  boxShadow: '0 2px 4px rgba(74, 222, 128, 0.3)',
                                  '& .MuiChip-label': { px: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }
                                }}
                              />
                            )}
                            {isReplyingToHuman && (
                              <Chip
                                icon={<span style={{ fontSize: '0.8rem' }}>↪</span>}
                                label="Replying to You"
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.25) 0%, rgba(74, 222, 128, 0.15) 100%)',
                                  color: '#4ade80',
                                  border: '1.5px solid rgba(74, 222, 128, 0.5)',
                                  boxShadow: '0 2px 4px rgba(74, 222, 128, 0.2)',
                                  animation: 'pulse 2s ease-in-out infinite',
                                  '@keyframes pulse': {
                                    '0%, 100%': { opacity: 0.9 },
                                    '50%': { opacity: 1, boxShadow: '0 2px 8px rgba(74, 222, 128, 0.4)' }
                                  },
                                  '& .MuiChip-label': { px: 0.75, textTransform: 'uppercase', letterSpacing: '0.03em' },
                                  '& .MuiChip-icon': { ml: 0.5, color: '#4ade80' }
                                }}
                              />
                            )}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                            {msg.round && msg.round !== "final" ? (
                              <Chip
                                label={`Round ${msg.round}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  background: `linear-gradient(135deg, ${agentColor}30 0%, ${agentColor}20 100%)`,
                                  color: agentColor,
                                  border: `1.5px solid ${agentColor}50`,
                                  boxShadow: `0 2px 4px ${agentColor}20`,
                                  '& .MuiChip-label': { px: 1, textTransform: 'uppercase', letterSpacing: '0.03em' }
                                }}
                              />
                            ) : msg.round === "final" ? (
                              <Chip
                                label="Final Evaluation"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  background: 'linear-gradient(135deg, #e8be42 0%, rgba(232, 191, 66, 0.85) 100%)',
                                  color: '#000',
                                  border: '1.5px solid #FFD700',
                                  boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4), inset 0 1px 1px rgba(255,255,255,0.5)',
                                  '& .MuiChip-label': { px: 1, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 800 }
                                }}
                              />
                            ) : (
                              <Chip
                                label={`Turn ${msg.turn}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  background: `linear-gradient(135deg, ${agentColor}30 0%, ${agentColor}20 100%)`,
                                  color: agentColor,
                                  border: `1.5px solid ${agentColor}50`,
                                  boxShadow: `0 2px 4px ${agentColor}20`,
                                  '& .MuiChip-label': { px: 1, textTransform: 'uppercase', letterSpacing: '0.03em' }
                                }}
                              />
                            )}
                            {msg.cost !== undefined && (
                              <Chip
                                label={`$${(msg.cost / 100).toFixed(5)}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)',
                                  color: '#26bf94',
                                  border: '1.5px solid rgba(76, 175, 80, 0.4)',
                                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.15)',
                                  '& .MuiChip-label': { px: 0.75, fontFamily: 'monospace' }
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Message Content */}
                      <Box sx={{
                        p: 2,
                        borderRadius: '8px',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '0.875rem',
                        lineHeight: 1.7
                      }}>
                        {/* Typing indicator for streaming messages */}
                        {msg.streaming && msg.message.length === 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ color: agentColor, fontSize: '0.875rem', fontStyle: 'italic' }}>
                              Thinking
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {[0, 1, 2].map((dot) => (
                                <Box
                                  key={dot}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: agentColor,
                                    animation: 'pulse 1.4s ease-in-out infinite',
                                    animationDelay: `${dot * 0.2}s`,
                                    '@keyframes pulse': {
                                      '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                      '40%': { opacity: 1, transform: 'scale(1.2)' }
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {(() => {
                          const result = formatContent(msg.message, agentColor);
                          if (!result) {
                            console.error("formatContent returned null for message:", msg);
                            return <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Error rendering message</Typography>;
                          }
                          const { content, urls } = result;
                          const sources = msg.sources || [];

                          // Combine URLs from message with explicit sources AND context pack sources
                          const allSources = [...sources];

                          // Add context pack sources for all messages (agents use shared context)
                          if (contextPack && contextPack.sources && msg.agent !== 'consensus') {
                            contextPack.sources.forEach(contextSource => {
                              if (!allSources.find(s => s.ref === contextSource.ref)) {
                                allSources.push(contextSource);
                              }
                            });
                          }

                          // Add extracted URLs
                          urls.forEach(url => {
                            if (!allSources.find(s => s.ref === url)) {
                              allSources.push({ kind: 'url', ref: url });
                            }
                          });

                          return (
                            <>
                              {content}

                              {/* Sources Section */}
                              {allSources.length > 0 && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${agentColor}30` }}>
                                  <Typography variant="caption" sx={{
                                    fontWeight: 700,
                                    color: agentColor,
                                    display: 'block',
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    fontSize: '0.7rem'
                                  }}>
                                    Sources ({allSources.length})
                                  </Typography>
                                  {allSources.map((source, i) => {
                                    // Handle different source formats
                                    const sourceKind = source.kind || 'url';
                                    const sourceRef = source.ref || source;
                                    const sourceTitle = source.title || null;

                                    return (
                                      <Box key={i} sx={{ mb: 0.5 }}>
                                        {sourceKind === 'url' && (
                                          <Link
                                            href={sourceRef.startsWith('http') ? sourceRef : `https://${sourceRef}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                              color: 'rgba(255,255,255,0.6)',
                                              fontSize: '0.75rem',
                                              textDecoration: 'none',
                                              '&:hover': { color: agentColor }
                                            }}
                                          >
                                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                                            {(() => {
                                              // Show title if available, otherwise extract from URL
                                              if (sourceTitle) {
                                                return sourceTitle;
                                              }
                                              try {
                                                // Handle web:search_results format
                                                if (sourceRef.includes('web:')) {
                                                  return 'Web Search Results';
                                                }
                                                return new URL(sourceRef.startsWith('http') ? sourceRef : `https://${sourceRef}`).hostname.replace('www.', '');
                                              } catch {
                                                return sourceRef.includes('web:') ? 'Web Search Results' : sourceRef;
                                              }
                                            })()}
                                          </Link>
                                        )}
                                        {sourceKind === 'memory' && (
                                          <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            color: 'rgba(255,255,255,0.6)',
                                            fontSize: '0.75rem'
                                          }}>
                                            <MemoryIcon sx={{ fontSize: 14 }} />
                                            {sourceTitle || (sourceRef.includes('memory:')
                                              ? sourceRef.split('memory:')[1].replace(/_/g, ' ')
                                              : sourceRef)}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Paper>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* Single Pause/Input Toggle Button */}
      <AnimatePresence>
        {isRunning && !humanInputOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          >
            <IconButton
              onClick={handlePauseResume}
              sx={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                width: 56,
                height: 56,
                background: isPaused 
                  ? 'linear-gradient(135deg, #4bb58e 0%, #327d62 100%)'
                  : 'linear-gradient(135deg, #D183C9 0%, #a56ca0 100%)',
                color: 'white',
                boxShadow: isPaused
                  ? '0 8px 24px rgba(75, 181, 142, 0.43)'
                  : '0 8px 24px rgba(209, 131, 201, 0.42)',
                zIndex: 1000,
                '&:hover': {
                  background: isPaused
                  ? 'linear-gradient(135deg, #4bb58e 0%, #36a47cff 100%)'
                  : 'linear-gradient(135deg, #D183C9 0%, #a56ca0 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: isPaused
                    ? '0 12px 28px rgba(75, 181, 142, 0.42)'
                    : '0 12px 28px rgba(209, 131, 201, 0.43)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Human Input Dialog */}
      <AnimatePresence>
        {humanInputOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px'
            }}
            onClick={() => {
              setHumanInputOpen(false);
              setHumanInputText("");
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%', maxWidth: '600px' }}
            >
              <Paper
                sx={{
                  p: 3,
                  background: 'rgba(40, 40, 40, 0.98)',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(74, 222, 128, 0.3)',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: '#4ade80', fontWeight: 600 }}>
                  💬 Add Your Input to the Debate
                </Typography>
                
                {/* Show timing info */}
                {currentTurn && (
                  <Alert severity="info" sx={{ 
                    mb: 2, 
                    background: 'rgba(58, 208, 222, 0.1)',
                    border: '1px solid rgba(58, 208, 222, 0.3)',
                    '& .MuiAlert-message': { color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }
                  }}>
                    <strong>Round {currentTurn.turn || 1} of {rounds}</strong>
                    <br />
                    Your input will be seen by the next agent to speak.
                  </Alert>
                )}
                
                <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
                  Your input will be considered by the next agent while they continue their analysis.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={humanInputText}
                  onChange={(e) => setHumanInputText(e.target.value)}
                  placeholder="Type your thoughts, corrections, or additional context..."
                  autoFocus
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'white',
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      handleHumanInput();
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setHumanInputOpen(false);
                      setHumanInputText("");
                      // Resume without input
                      if (isPausedRef.current) {
                        handlePauseResume();
                      }
                    }}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.4)',
                        background: 'rgba(255,255,255,0.05)'
                      }
                    }}
                  >
                    Cancel & Resume
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleHumanInput}
                    disabled={!humanInputText.trim()}
                    sx={{
                      background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      }
                    }}
                  >
                    Submit & Resume (Ctrl+Enter)
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default NegotiationRoom;
